'use client';

import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { useQuery } from '@parkchirp/api-client';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { useDebounce } from '../../hooks';
import { LIST_HOURLY_ACCOUNTS_V2 } from '../../queries';
import { HourlyAccount, HourlyQueryContextEnum } from '../../types';

type Option = HourlyAccount & {
	label: string;
	value: string;
	key: string;
};

const noneOption: Partial<Option> = {
	label: 'None',
	value: 'None',
	key: 'none',
};

interface HourlyAccountAutocompleteProps {
	organizationId: number;
	hourlyAccountId: string;
	hourlyAccountPid?: string;
	hourlyAccountName?: string;
	label?: string;
	disabled?: boolean;
	disableClearable?: boolean;
	error?: boolean;
	required?: boolean;
	onChange?: (value: Option) => void;
	loadingDependency?: boolean;
	size?: 'small' | 'medium';
}

export const HourlyAccountAutocomplete: FC<HourlyAccountAutocompleteProps> = ({
	organizationId,
	hourlyAccountId,
	hourlyAccountPid,
	hourlyAccountName,
	label,
	disabled = false,
	disableClearable = false,
	error = false,
	required = false,
	onChange,
	loadingDependency = false,
	size = 'medium',
}) => {
	const defaultOption: Partial<Option> = useMemo(() => {
		if (!hourlyAccountId || !hourlyAccountPid || !hourlyAccountName) {
			return noneOption;
		}

		return {
			label: `(PID-${hourlyAccountPid}) ${hourlyAccountName}`,
			value: hourlyAccountId,
			key: hourlyAccountId,
		};
	}, [hourlyAccountId, hourlyAccountPid, hourlyAccountName]);

	const [options, setOptions] = useState<Option[]>([defaultOption as Option]);

	const [searchTerm, setSearchTerm] = useState('');
	const [open, setOpen] = useState(false);
	const [selectedOption, setSelectedOption] = useState<Option>(defaultOption as Option);

	useEffect(() => {
		setOptions([defaultOption as Option]);
		setSelectedOption(defaultOption as Option);
	}, [defaultOption, setSelectedOption]);

	const debouncedOnChange = useDebounce(setSearchTerm, 800);

	const onInputChange = useCallback(
		(_, value: string) => {
			if (open && value !== searchTerm) {
				debouncedOnChange(value);
			}
		},
		[open, searchTerm, debouncedOnChange],
	);

	const onAutocompleteChange = useCallback(
		(option: Option) => {
			setSelectedOption(option);
			onChange(option);
		},
		[setSelectedOption, onChange],
	);

	const getQueryFilters = () => [
		{
			propertyName: 'name',
			op: 'contains',
			value: searchTerm,
		},
	];

	const { loading } = useQuery(LIST_HOURLY_ACCOUNTS_V2, {
		variables: {
			offset: 0,
			limit: 10,
			queryContext: HourlyQueryContextEnum.AUTOCOMPLETE,
			filters: getQueryFilters(),
			organizationId,
			order: {
				field: 'pid',
				sort: 'desc',
			},
		},
		onCompleted: (data) => {
			const newOptions: Option[] = data.listHourlyAccounts.hourlyAccounts.map((hourlyAccount) => ({
				...hourlyAccount,
				label: `(PID-${hourlyAccount.pid}) ${hourlyAccount.name}`,
				value: hourlyAccount.id,
				key: hourlyAccount.id,
			}));
			setOptions(newOptions);
		},
		skip: !searchTerm || searchTerm === 'None',
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
		},
	});

	const isLoading = loading || loadingDependency;

	return (
		<Autocomplete
			open={open}
			onOpen={() => {
				setOpen(true);
			}}
			onClose={() => {
				setOpen(false);
			}}
			value={selectedOption}
			onChange={(_, value) => {
				onAutocompleteChange(value);
			}}
			onInputChange={onInputChange}
			options={options}
			isOptionEqualToValue={(option, { value }) => option.value === value}
			loading={isLoading}
			filterOptions={(x) => x}
			forcePopupIcon={false}
			fullWidth
			renderOption={(props, option) => (
				<li {...props} key={option.key}>
					{option.label}
				</li>
			)}
			renderInput={(params) => (
				<TextField
					{...params}
					label={label || ''}
					required={required}
					error={error}
					InputProps={{
						...params.InputProps,
						endAdornment: (
							<>
								{isLoading ? (
									<CircularProgress color='inherit' size={20} data-cy='loading-indicator' />
								) : null}
								{params.InputProps.endAdornment}
							</>
						),
					}}
				/>
			)}
			disabled={disabled}
			readOnly={disabled}
			disableClearable={disableClearable}
			sx={{
				'& .MuiInputBase-root': {
					paddingRight: '39px !important',
				},
			}}
			size={size}
		/>
	);
};
